// One place a screen outside Lead Management reads a lane count (lead_lane_counts, Maverick's view; signed-in read), so a banner or badge can never show a number
// Lead Management does not (the Dashboard said "37 new arrivals waiting" while the New Arrivals tile said 0: it counted a raw workflow flag, not the lane).
// A lane with no row has nobody in it: 0. null means the read failed or is not known yet, and the caller shows nothing rather than a guess.
import { useEffect, useState } from 'react';
import { supabase } from './supabase';

export async function readLaneCount(lane) {
  try {
    const { data, error } = await supabase.from('lead_lane_counts').select('lane, n').eq('lane', lane);
    if (error) return null;
    const row = (data || [])[0];
    return row ? Number(row.n) || 0 : 0;
  } catch (e) {
    return null;
  }
}

// The count for one lane. Refreshes when the app returns to the foreground. null until known (or when the read fails).
export function useLaneCount(lane, enabled = true) {
  const [count, setCount] = useState(null);
  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    const load = async () => {
      const n = await readLaneCount(lane);
      if (live && n !== null) setCount(n);
    };
    load();
    const onShow = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onShow);
    return () => { live = false; document.removeEventListener('visibilitychange', onShow); };
  }, [lane, enabled]);
  return count;
}

// Several lanes at once: { [lane]: n } with a lane that has no row as 0, or null when the read failed.
export async function readLaneCounts(lanes) {
  const want = Array.from(new Set((lanes || []).filter(Boolean)));
  if (want.length === 0) return {};
  try {
    const { data, error } = await supabase.from('lead_lane_counts').select('lane, n').in('lane', want);
    if (error) return null;
    const out = {};
    want.forEach((l) => { out[l] = 0; });
    (data || []).forEach((r) => { out[r.lane] = Number(r.n) || 0; });
    return out;
  } catch (e) {
    return null;
  }
}

// The counts for the lanes a board's widgets say they hide on, refreshed when the app returns to the foreground. null until known.
export function useLaneCounts(lanes, enabled = true) {
  const key = Array.from(new Set((lanes || []).filter(Boolean))).sort().join('|');
  const [counts, setCounts] = useState(null);
  useEffect(() => {
    if (!enabled || !key) return undefined;
    let live = true;
    const load = async () => {
      const c = await readLaneCounts(key.split('|'));
      if (live && c) setCounts(c);
    };
    load();
    const onShow = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onShow);
    return () => { live = false; document.removeEventListener('visibilitychange', onShow); };
  }, [key, enabled]);
  return counts;
}
